/**
 * admin-viewing-map.js
 */

import React from 'react';
import axios from 'axios';
import { 
  Box, 
  FormControl, 
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  ListItemText,
  Divider,
  Chip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { format, formatISO, parseISO, addMonths } from 'date-fns';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import Spinner from 'components/spinner';
import FeatureBox from 'components/feature-box';
import UserSearch from 'components/user-search';
import { TRACK_TYPE, TRACK_TYPE_ICON, TRACK_TYPE_LABEL } from '../tracking-common';
import { ViewingMapProvider } from '../map/viewing-map-context';
import { MODE, AdminMapContext, AdminMapProvider } from './admin-map-context';
import AdminMapControls from './admin-map-controls';
import TrackInfo from './track-info';
import AdminViewingMap from './admin-viewing-map';


const TRACK_ENDPOINT = {
  [TRACK_TYPE.DEPLOYMENT]: (trackId) => `/deployment-track/${trackId}`,
  [TRACK_TYPE.LEAD]: (trackId) => `/exercise-lead-track/${trackId}`,
  [TRACK_TYPE.FOLLOW]: (trackId) => `/exercise-follow-track/${trackId}`,
};


const RECORD_ENDPOINT = {
  [TRACK_TYPE.DEPLOYMENT]: (trackingId) => `/deployment/tracking/${trackingId}`,
  // [TRACK_TYPE.LEAD]: (id) => `/exercise-lead-track/${trackId}`,
  [TRACK_TYPE.FOLLOW]: (trackingId) => `/training/answer/tracking/${trackingId}`,
}


const AdminMap = (props) => {
  const history = useHistory();
  const params = useParams();
  const userId = +params.userId || null;
  const trackType = params.trackType || null;
  const trackId = +params.trackId || null;
  const { admin } = React.useContext(UserContext);
  const {
    mode,
    setMode,
    isLoading,
    setIsLoading,
    user,
    setUser,
    userTracks,
    setUserTracks,
    userTrack,
    setUserTrack,
    userTrackRecord,
    setUserTrackRecord,
    isFollowTrackVisible,
    setIsFollowTrackVisible,
    isLeadTrackVisible,
    setIsLeadTrackVisible,
  } = React.useContext(AdminMapContext);
  const [selectedTrack, setSelectedTrack] = React.useState('');
  const [filteredTracks, setFilteredTracks] = React.useState();
  const basePath = '/admin/tracking';


  // query user tracks
  const getUserTracks = React.useCallback(async (user) => {
    if (!user) {
      return;
    }

    setIsLoading('userTracks', true);
    const startDate = formatISO(addMonths(new Date(), -12));
    const response = await axios.get(`/track/all/${user.id}?startDate=${startDate}`, { baseURL: process.env.REACT_APP_TRACKING_HOST });
    if (response.isSuccess) {
      setUserTracks(response.data);
    }
    setIsLoading('userTracks', false);
  }, [setIsLoading, setUserTracks]);


  // user-setter. must return a bool so indicate validity.
  const onSetUser = React.useCallback((user) => {
    setUser(user);
    setSelectedTrack('');
    getUserTracks(user);

    if (!user) {
      history.push(basePath);
    } else if (userId !== user.id) {
      history.push(`${basePath}/${user.id}`);
    }
  }, [setUser, history, userId, getUserTracks]);


  // lookup user specified by userId url param
  React.useEffect(() => {
    if (userId && userId !== user?.id) {
      (async() => {
        setIsLoading('userTracks', true);
        const response = await axios.get(`/account/${userId}`);
        if (response.isSuccess) {
          onSetUser(response.data);
        }
        setIsLoading('userTracks', false);
      })();
    }
  }, [userId, user, setIsLoading, onSetUser]);


  // query selected track data
  const getTrackData = React.useCallback(async (track) => {
    setIsLoading('userTrack', true);
    const trackResponse = await axios.get(TRACK_ENDPOINT[track.type](track.id), { baseURL: process.env.REACT_APP_TRACKING_HOST });
    if (trackResponse.isSuccess) {
      const userTrack = trackResponse.data;
      let userTrackRecord = null;

      // query record data
      if ([TRACK_TYPE.DEPLOYMENT, TRACK_TYPE.FOLLOW].includes(userTrack.type)) {
        const trackingId = userTrack.type === TRACK_TYPE.DEPLOYMENT
          ? userTrack.deploymentTrackingId
          : userTrack.completionTrackingId;
        const recordResponse = await axios.get(RECORD_ENDPOINT[userTrack.type](trackingId));
        if (recordResponse.isSuccess) {
          userTrackRecord = recordResponse.data;
        }
      } else if (track.type === TRACK_TYPE.LEAD) {
        userTrackRecord = true;
      }
      
      setUserTrack(userTrack);
      setUserTrackRecord(userTrackRecord);
    }

    setIsLoading('userTrack', false);
  }, [setIsLoading, setUserTrack, setUserTrackRecord]);


  // track-setting helper
  const onSetTrack = React.useCallback(async (track) => {
    setSelectedTrack(track);
    if (track) {
      await getTrackData(track);
    }

    if (!track) {
      history.push(`${basePath}/${user.id}`);
    } else if (trackId !== track.id) {
      history.push(`${basePath}/${user.id}/${track.type}/${track.id}`);
    }
  }, [trackId, user, history, getTrackData]);


  // handle changes to track url params
  React.useEffect(() => {
    if (trackId && user && userTracks && trackId !== userTrack?.id) {
      setSelectedTrack(userTracks.find(t => t.id === trackId));
      getTrackData({ type: trackType, id: trackId });
    } else if (!trackId) {
      setSelectedTrack('');
      setUserTrack('');
      setUserTrackRecord();
    }
  }, [trackType, trackId, user, userTracks, userTrack, getTrackData, setUserTrack, setUserTrackRecord]);


  // filter tracks based on mode
  React.useEffect(() => {
    if (!userTracks) {
      return;
    }

    if (mode === MODE.VIEW) {
      // filter out lead tracks if they are already part of a lead/follow combo
      const filteredTracks = [];
      const followedIds = [];
      const allIds = [];
      userTracks.forEach(track => {
        if (track.type === TRACK_TYPE.DEPLOYMENT) {
          allIds[track.id] = true;
          filteredTracks.push(track);
        } else if (track.type === TRACK_TYPE.FOLLOW) {
          allIds[track.id] = true;
          filteredTracks.push(track);
          if (track.leadTrackId) {
            followedIds[track.leadTrackId] = true;
          }
        } else if (track.type === TRACK_TYPE.LEAD && !followedIds[track.id]) {
          allIds[track.id] = true;
          filteredTracks.push(track);
        }
      });
      setFilteredTracks(filteredTracks);

      // check if we're selecting an invalid track
      if (!allIds[selectedTrack.id]) {
        let associatedTrack = '';
        if (selectedTrack.type === TRACK_TYPE.LEAD) {
          // look for associated follow track
          associatedTrack = userTracks.find(track => track.type === TRACK_TYPE.FOLLOW && track.leadTrackId === selectedTrack.id);
        }
        onSetTrack(associatedTrack);
      }
    } else if (mode === MODE.FILTER) {
      setFilteredTracks(userTracks);
    }
  }, [mode, userTracks, selectedTrack, onSetTrack]);


  if (!admin) {
    return null;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex' }}>

      {/* controls */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        width: 320, 
        p: 2, 
        textAlign: 'left',
      }}>
        {/* mode */}
        <Typography variant='caption' sx={{ mb: 0.5 }}>Mode</Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size='small'
          value={mode}
          onChange={(e, v) => {
            if (v) {
              setIsFollowTrackVisible(true);
              setIsLeadTrackVisible(true);
              setMode(v);
            }
          }}
          sx={{ mb: 3 }}
        >
          <ToggleButton value={MODE.VIEW}>View</ToggleButton>
          <ToggleButton value={MODE.FILTER}>Filter</ToggleButton>
        </ToggleButtonGroup>

        {/* name/email user search */}
        <UserSearch role={Types.Role.HANDLER} value={user} onSelect={onSetUser} sx={{ mb: 3 }} />

        {/* user's tracklist */}
        <Box sx={{ mb: 2, display: 'inherit', flexDirection: 'inherit' }}>
          {isLoading.userTracks &&
          <TextField
            size='small'
            disabled
            value={'Loading Tracks...'}
            InputProps={{ endAdornment: <Spinner size={25} /> }}
          />}
          {filteredTracks && !isLoading.userTracks &&
          <FormControl size='small'>
            <InputLabel id='user-track-label' shrink={filteredTracks.length === 0 ? true : undefined}>Track</InputLabel>
            <Select 
              labelId='user-track-label'
              id='user-track'
              label='Track'
              value={selectedTrack}
              disabled={filteredTracks.length === 0}
              displayEmpty={filteredTracks.length === 0}
              sx={{ 
                '& .MuiSelect-select .MuiListItemText-root': { my: 0 }, 
                '& .MuiSelect-select .MuiChip-root': { backgroundColor: 'sys.surfaceContainerHigh' },
              }}
              onChange={(e, v) => {
                onSetTrack(e.target.value);
              }}
            >
              {filteredTracks.length === 0 && <MenuItem value=''>No Tracks Found</MenuItem>}
              {filteredTracks.map(track => (
                <MenuItem 
                  key={track.id}
                  value={track} 
                >
                  <Box 
                    className='flex-hc' 
                    sx={{ '& .MuiListItemText-secondary': { fontSize: 'sizes.1' }}}
                  >
                    <Icon type={TRACK_TYPE_ICON[track.type]} sx={{ mr: 2 }} />
                    <ListItemText 
                      primary={<Box className='flex-hc' sx={{ height: 24 }}>
                        {TRACK_TYPE_LABEL[track.type]}
                        {track.type === TRACK_TYPE.FOLLOW && track.leadTrackId && mode === MODE.VIEW &&
                        <Chip size='small' label='+ Lay' sx={{ ml: 1, backgroundColor: 'sys.surfaceContainer', height: 21 }} />}
                      </Box>} 
                      secondary={format(parseISO(track.dateTimeCreated), 'MMM dd, yyyy - HH:mm:ss')} 
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>}
        </Box>

        {/* track info */}
        {userTrack &&
        <FeatureBox loading={isLoading.userTrack}>
          <TrackInfo 
            track={userTrack} 
            trackRecord={userTrackRecord} 
            checkBoxProps={Boolean(userTrack?.leadTrack && mode === MODE.VIEW) 
              ? { checked: isFollowTrackVisible, onChange: setIsFollowTrackVisible } 
              : null}
          />

          {/* lead track info */}
          {userTrack?.leadTrack && mode === MODE.VIEW && 
          <>
            <TrackInfo 
              track={userTrack.leadTrack} 
              checkBoxProps={{ checked: isLeadTrackVisible, onChange: setIsLeadTrackVisible }}
            />
          </>}
        </FeatureBox>}

        {userTrack &&
        <> 
          <Divider sx={{ mb: 2 }} />
          <AdminMapControls />
        </>}
      </Box>

      <Box sx={{
        flex: 1,
        backgroundColor: 'sys.surfaceContainer'
      }}>
        <AdminViewingMap />
      </Box>
    </Box>
  );
}


export default function AdminMapWrap(props) {
  return (
    <AdminMapProvider>
      <ViewingMapProvider>
        <AdminMap />
      </ViewingMapProvider>
    </AdminMapProvider>
  );
}